/** Central navigation registry for NavRail, MenuBar, and CommandPalette. */

export type NavGroup = "trading" | "operations" | "risk" | "admin";

export type NavItem = {
  id: string;
  label: string;
  path: string;
  icon?: string;
  group?: NavGroup;
  children?: NavItem[];
  shortcut?: string;
};

export type QuickAction = {
  id: string;
  label: string;
  path?: string;
  onAction?: "open-layouts" | "open-palette";
  description?: string;
};

export const NAV_GROUP_LABELS: Record<NavGroup, string> = {
  trading: "Trading",
  operations: "Operations",
  risk: "Risk & reporting",
  admin: "Admin",
};

export const MODULES: NavItem[] = [
  { id: "dash", label: "Dashboard", path: "/", icon: "▣", group: "trading", shortcut: "⌘1" },
  { id: "trades", label: "Trades", path: "/trades", icon: "⇄", group: "trading" },
  { id: "curves", label: "Curves", path: "/curves", icon: "∿", group: "trading" },
  {
    id: "data-hub",
    label: "DataHub",
    path: "/data-hub",
    icon: "⧉",
    group: "operations",
  },
  { id: "tradepad", label: "Tradepad", path: "/tradepad", icon: "◈", group: "trading" },
  {
    id: "cash",
    label: "Cash",
    path: "/cash",
    icon: "₵",
    group: "operations",
    children: [
      { id: "cash-bal",   label: "Balances", path: "/cash/balances" },
      { id: "cash-ocp",   label: "Open Currency Position", path: "/cash/ocp" },
      { id: "cash-pay",   label: "Payment Orders", path: "/cash/payments" },
      { id: "cash-anl",   label: "Analytics", path: "/cash/analytics" },
      { id: "cash-usage", label: "Cash Usage", path: "/cash/usage" },
      { id: "cash-ladder", label: "Cash Ladder", path: "/cash/ladder" },
      { id: "cash-recon", label: "Statement Reconciliation", path: "/cash/reconciliation" },
    ],
  },
  {
    id: "instruments",
    label: "Instruments",
    path: "/instruments",
    icon: "⬡",
    group: "operations",
  },
  {
    id: "middle-office",
    label: "Middle Office",
    path: "/middle-office",
    icon: "⚑",
    group: "operations",
    children: [
      { id: "mo-booking", label: "Booking Issues", path: "/middle-office?focus=mo-booking-issues" },
    ],
  },
  {
    id: "reports",
    label: "Reports",
    path: "/reports",
    icon: "□",
    group: "risk",
    children: [
      { id: "reports-cat", label: "Catalogue", path: "/reports" },
      { id: "reports-pnl-explain", label: "P&L Explain", path: "/reports/pnl-explain" },
      { id: "reports-rates-stress", label: "Rates Stress", path: "/reports/rates-stress" },
    ],
  },
  {
    id: "risk",
    label: "Risk",
    path: "/risk",
    icon: "△",
    group: "risk",
    children: [{ id: "risk-stress", label: "Stress", path: "/risk/stress" }],
  },
  { id: "recon", label: "Recon", path: "/recon", icon: "✒", group: "risk" },
  { id: "admin", label: "Admin", path: "/admin", icon: "⚙", group: "admin" },
];

export const QUICK_ACTIONS: QuickAction[] = [
  { id: "qa-trades", label: "Go to Trades", path: "/trades", description: "Open trade blotter" },
  { id: "qa-pay", label: "New payment order", path: "/cash/payments", description: "Cash payment orders" },
  { id: "qa-cash-ladder", label: "Cash Ladder", path: "/cash/ladder", description: "Forward cash ladder view" },
  { id: "qa-cash-recon", label: "Statement Reconciliation", path: "/cash/reconciliation", description: "Treasury CSV upload" },
  { id: "qa-margin-rates", label: "Margin Rates", path: "/admin", description: "Manage CCP IM rates (Admin)" },
  { id: "qa-pnl-explain", label: "P&L Explain", path: "/reports/pnl-explain", description: "Daily P&L explain matrix" },
  { id: "qa-stress", label: "Stress tests", path: "/risk/stress", description: "Scenario matrix, VaR, limits, quick runs" },
  { id: "qa-rates-stress", label: "Rates Stress", path: "/reports/rates-stress", description: "Bond curve stress and VaR" },
  { id: "qa-dash", label: "Dashboard", path: "/", description: "Live positions" },
  { id: "qa-palette", label: "Command palette", onAction: "open-palette", description: "⌘K" },
  { id: "qa-layouts", label: "Grid layouts", onAction: "open-layouts", description: "Save / load column layouts" },
];

export type PaletteCommand = {
  id: string;
  label: string;
  path: string;
  group?: string;
};

/** Top-level modules for the icon rail (no children). */
export function railModules(): NavItem[] {
  return MODULES;
}

/** Flat list of navigable destinations for command palette search. */
export function flattenNav(items: NavItem[] = MODULES): NavItem[] {
  const out: NavItem[] = [];
  for (const item of items) {
    out.push(item);
    if (item.children?.length) {
      for (const child of item.children) {
        out.push({ ...child, label: `${item.label} › ${child.label}` });
      }
    }
  }
  return out;
}

export function paletteCommands(): PaletteCommand[] {
  const nav = flattenNav().map((n) => ({
    id: n.id,
    label: n.label,
    path: n.path,
    group: n.group,
  }));
  const actions = QUICK_ACTIONS.filter((a) => a.path).map((a) => ({
    id: a.id,
    label: a.label,
    path: a.path!,
    group: "actions" as const,
  }));
  const seen = new Set<string>();
  return [...nav, ...actions].filter((c) => {
    const key = `${c.path}:${c.label}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function modulesByGroup(): { group: NavGroup; label: string; items: NavItem[] }[] {
  const groups: NavGroup[] = ["trading", "operations", "risk", "admin"];
  return groups.map((group) => ({
    group,
    label: NAV_GROUP_LABELS[group],
    items: MODULES.filter((m) => m.group === group),
  }));
}

export type Breadcrumb = { label: string; path?: string };

/** Resolve breadcrumb from pathname (e.g. `/cash/payments` → Cash › Payment Orders). */
export function breadcrumbFromPath(pathname: string): Breadcrumb[] {
  const crumbs: Breadcrumb[] = [{ label: "Atlas", path: "/" }];

  if (pathname === "/") {
    crumbs.push({ label: "Dashboard" });
    return crumbs;
  }

  if (pathname === "/account" || pathname.startsWith("/account/")) {
    crumbs.push({ label: "Account", path: "/account" });
    if (pathname.startsWith("/account/security")) crumbs.push({ label: "Security" });
    else if (pathname.startsWith("/account/sessions")) crumbs.push({ label: "Sessions" });
    else if (pathname.startsWith("/account/integrations")) crumbs.push({ label: "Integrations" });
    else crumbs.push({ label: "Profile" });
    return crumbs;
  }

  for (const mod of MODULES) {
    if (mod.path === "/" ? pathname === "/" : pathname === mod.path || pathname.startsWith(`${mod.path}/`)) {
      crumbs.push({ label: mod.label, path: mod.path });
      if (mod.children) {
        // Most specific match wins: a child at the module root (Reports › Catalogue)
        // must not shadow a deeper sibling (Reports › P&L Explain).
        const child = mod.children
          .filter((c) => pathname === c.path || pathname.startsWith(`${c.path}/`))
          .sort((x, y) => y.path.length - x.path.length)[0];
        if (child) crumbs.push({ label: child.label });
      }
      return crumbs;
    }
  }

  crumbs.push({ label: pathname });
  return crumbs;
}

export function isNavPathActive(pathname: string, itemPath: string): boolean {
  if (itemPath === "/") return pathname === "/";
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}
